import { useCallback, useEffect, useRef, useState } from "react";
import { getScripts } from "../services/script.service.js";

const useScriptVersions = (
  animationId,
  pushLog,
  defaultScript,
  externalScriptState = null,
  options = {},
) => {
  const { onLoadedScriptChange } = options;
  const onLoadedScriptChangeRef = useRef(onLoadedScriptChange);
  const [internalScript, setInternalScript] = useState(defaultScript);
  const hasExternalScriptState =
    externalScriptState &&
    typeof externalScriptState.setScript === "function" &&
    typeof externalScriptState.script === "string";
  const script = hasExternalScriptState
    ? externalScriptState.script
    : internalScript;
  const setScript = hasExternalScriptState
    ? externalScriptState.setScript
    : setInternalScript;
  const [isLoadingScript, setIsLoadingScript] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [totalVersions, setTotalVersions] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageRef = useRef(1);

  useEffect(() => {
    onLoadedScriptChangeRef.current = onLoadedScriptChange;
  }, [onLoadedScriptChange]);

  const loadVersion = useCallback(
    async (pageNumber) => {
      if (!animationId) return;
      const originalPage = pageRef.current;
      pageRef.current = pageNumber;
      setCurrentPage(pageNumber);
      setIsLoadingScript(true);
      try {
        const res = await getScripts(animationId, {
          limit: 1,
          page: pageNumber,
          sortBy: "updatedAt",
          sortOrder: "desc",
        });

        if (!res?.records?.length) {
          setScript(defaultScript);
          onLoadedScriptChangeRef.current?.(defaultScript);
          pageRef.current = 1;
          setCurrentPage(1);
          setHasPrev(false);
          setHasNext(false);
          setTotalVersions(0);
          return;
        }

        const nextScript = res.records[0]?.text || defaultScript;
        setScript(nextScript);
        onLoadedScriptChangeRef.current?.(nextScript);
        setHasPrev(pageNumber < (res.totalPages || 0));
        setHasNext(pageNumber > 1);
        setTotalVersions(res.totalPages || 0);
      } catch (error) {
        pageRef.current = originalPage;
        setCurrentPage(originalPage);
        pushLog("error", error.message || "Failed to load script version");
      } finally {
        setIsLoadingScript(false);
      }
    },
    [animationId, defaultScript, pushLog, setScript],
  );

  useEffect(() => {
    if (!animationId) {
      setScript(defaultScript);
      setHasPrev(false);
      setHasNext(false);
      setTotalVersions(0);
      pageRef.current = 1;
      setCurrentPage(1);
      return;
    }
    loadVersion(1);
  }, [animationId, defaultScript, loadVersion, setScript]);

  return {
    script,
    setScript,
    isLoadingScript,
    hasPrev,
    hasNext,
    totalVersions,
    currentPage,
    loadVersion,
    resetToDefault: () => setScript(defaultScript),
  };
};

export default useScriptVersions;
